import { useMemo, useState, useEffect } from 'react'

import { Box, Grid } from '@mui/material'
import { useFormContext, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  ControlledCheckbox,
  ControlledCurrencyAutocomplete,
  ControlledTextField,
  ControlledAutocomplete,
  LoadingBackdrop,
  ConfirmDialog,
} from 'components/common'
import { Roles } from 'constants/index'
import {
  CreateAndUpdateBookingDriverDtoInput,
  useGetAvailableSubDriversForBookingQuery,
  useGetCurrenciesQuery,
  useGetDriverCurrencyQuery,
} from 'generated/graphql'
import { useUserRole } from 'hooks'


interface IProps {
  loading: boolean
  driverId: number
  parentDriverId: number
  bookingId: number
}

export const EditDriverForm = (props: IProps) => {
  const { loading, driverId, parentDriverId, bookingId } = props

  const userRole = useUserRole()
  const [openInactiveDriverModal, setOpenInactiveDriverModal] = useState(false)
  const [lastValidDriverId, setLastValidDriverId] = useState<any>(null)

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<CreateAndUpdateBookingDriverDtoInput>()

  const selectedDriverId = useWatch({ control, name: 'driverId' })

  // Queries
  const { data: currenciesData, loading: currenciesLoading } = useGetCurrenciesQuery()

  const { data: driverCurrency } = useGetDriverCurrencyQuery({
    variables: { driverId: driverId },
    skip: !driverId,
  })

  const currencyId = driverCurrency?.driver?.currencyId

  const { data: subDriversData } = useGetAvailableSubDriversForBookingQuery({
    variables: {
      parentDriverId: Number(parentDriverId),
      currentDriverId: Number(driverId),
      bookingId: bookingId,
    },
    skip: !driverId,
    fetchPolicy: 'cache-and-network',
  })

  const subDrivers = useMemo(
    () => subDriversData?.availableSubDriversForBooking?.edges?.map((edge) => edge.node) || [],
    [subDriversData],
  )
  const hasSubDrivers = subDrivers.length > 0

  const driverOptions = useMemo(() => {
    return hasSubDrivers
      ? subDrivers.map((driver) => {
          const isCurrentDriver = Number(driver.id) === driverId
          return {
            value: Number(driver.id),
            label:
              driver.isActive || isCurrentDriver
                ? `${driver.name}${isCurrentDriver ? ' (Current)' : ''}`
                : `${driver.name} (Blocked)`,
            disabled: isCurrentDriver,
          }
        })
      : []
  }, [hasSubDrivers, subDrivers, driverId])

  const getInactiveDriverName = (driverId: any) => {
    if (!driverId || !subDrivers) return ''

    const selectedDriver = subDrivers.find((driver) => Number(driver.id) === Number(driverId))

    return selectedDriver ? selectedDriver.name : ''
  }

  useEffect(() => {
    if (selectedDriverId && subDrivers.length > 0) {
      const selectedDriver = subDrivers.find(
        (driver) => Number(driver.id) === Number(selectedDriverId),
      )

      if (selectedDriver && !selectedDriver.isActive) {
        setOpenInactiveDriverModal(true)
      } else {
        setLastValidDriverId(selectedDriverId)
      }
    }
  }, [selectedDriverId, subDrivers])

  const handleInactiveDriverConfirm = () => {
    setOpenInactiveDriverModal(false)
    setValue('driverId', lastValidDriverId || '')
  }

  const handleInactiveDriverCancel = () => {
    setOpenInactiveDriverModal(false)
    setValue('driverId', lastValidDriverId || '')
  }

  const readSelectedCurrency = (id: number) => {
    if (currencyId && id && currencyId !== id) {
      toast.warning(
        'The currency is already defined for the Supplier. Altering it could result in payment failure.',
      )
    }
  }

  const hasUserOwnerOrTCRole = userRole === Roles.owner || userRole === Roles.transportController

  return (
    <>
      <Box pt='6px'>
        <Grid container columnSpacing={3} rowSpacing={1.5}>
          <Grid item xs={12} sm={6}>
            {hasSubDrivers ? (
              <ControlledAutocomplete
                control={control}
                label='Driver'
                name='driverId'
                options={driverOptions}
                defaultValue=''
                error={!!errors.driverId}
                helperText={errors.driverId?.message}
                placeholder='Select a driver'
              />
            ) : (
              <ControlledTextField
                control={control}
                label='Driver'
                name='driver.name'
                readOnly
                defaultValue=''
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              label='Vehicle'
              name='vehicle.vehicleType.name'
              readOnly
              defaultValue=''
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              label='Contact'
              name='driverContact.name'
              readOnly
              defaultValue=''
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              label='Charge'
              name='charge'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              sanitizedNumber
              defaultValue=''
              readOnly={hasUserOwnerOrTCRole}
              error={!!errors.charge}
              helperText={errors.charge?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              label='Extra Pay'
              name='extraPayment'
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              sanitizedNumber
              defaultValue=''
              error={!!errors.extraPayment}
              helperText={errors.extraPayment?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledTextField
              control={control}
              label='Purch. Code'
              name='xeroPurchaseCode'
              defaultValue=''
              error={!!errors.xeroPurchaseCode}
              helperText={errors.xeroPurchaseCode?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <ControlledCurrencyAutocomplete
              control={control}
              options={currenciesData?.currencies || []}
              loading={currenciesLoading}
              error={!!errors.currencyId}
              helperText={errors.currencyId?.message}
              readSelectedCurrency={readSelectedCurrency}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledCheckbox
              control={control}
              label='Show Instruction to Driver'
              name='isShownInstruction'
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledTextField
              control={control}
              label='Instruction'
              name='instruction'
              multiline
              rows={7}
              error={!!errors.instruction}
              helperText={errors.instruction?.message}
            />
          </Grid>
        </Grid>
      </Box>

      <ConfirmDialog
        open={openInactiveDriverModal}
        onConfirm={handleInactiveDriverConfirm}
        onCancel={handleInactiveDriverCancel}
        setOpen={setOpenInactiveDriverModal}
        title='Driver Account Blocked'
        message={`The driver ${getInactiveDriverName(
          selectedDriverId,
        )} you selected is blocked due to not respecting the protocols. Please select an active driver to proceed.`}
        option1='Cancel'
        option2='Ok'
      />

      <LoadingBackdrop loading={loading} />
    </>
  )
}
